import styles from "@styles/HomeTaskList.module.css"
import { useTaskContext } from "@/app/context/TaskContext"
import { Task } from "@/app/helpers/database/tasks/saveTasks"
import { useEffect, useState } from "react"
import { TaskCard } from "./taskCards/TaskCard"
import { useCalendarContext } from "@/app/context/CalendarContext"
import { CalendarEvent } from "@/app/helpers/google/calendar/fetchCalendarEvents"
import clsx from "clsx"

export default function HomeTaskList() {
  const [mostImportantTask, setMostImportantTask] = useState<Task | null>(null)
  const [remainingTasksForToday, setRemainingTasksForToday] = useState<Task[]>(
    [],
  )
  const [calendarEventsForToday, setCalendarEventsForToday] = useState<
    CalendarEvent[]
  >([])
  const [calendarEventIdOfCurrentMeeting, setCalendarEventIdOfCurrentMeeting] =
    useState<string | null>(null)
  const [calendarEventsNeedingAction, setCalendarEventsNeedingAction] =
    useState<CalendarEvent[]>([])

  const calendarContext = useCalendarContext()
  const taskContext = useTaskContext()

  useEffect(() => {
    if (!calendarContext.calendarEvents) return

    // Get calendar events for today
    const todaysCalendarEvents = filterCalendarEventsForToday(
      calendarContext.calendarEvents.filter(
        (event) => event.myResponseStatus !== "declined",
      ),
    )
    setCalendarEventsForToday(todaysCalendarEvents)

    // Get calendar event that is currently in progress or starting in the next five minutes
    const currentCalendarEventId =
      checkIfCalendarEventCurrentlyInProgressOrStartingInNextFiveMinutes(
        todaysCalendarEvents,
      )
    setCalendarEventIdOfCurrentMeeting(currentCalendarEventId)

    // Get calendar events that need action
    const calendarEventsNeedingAction = calendarContext.calendarEvents.filter(
      (event) =>
        event.myResponseStatus === "needsAction" &&
        event.eventId !== currentCalendarEventId,
    )
    setCalendarEventsNeedingAction(calendarEventsNeedingAction)
  }, [calendarContext.calendarEvents])

  useEffect(() => {
    const tasks = taskContext?.tasks
    if (!tasks) return

    const uncompletedTasksSorted = tasks
      .filter((t) => !t.completed)
      .sort((a, b) =>
        b.priority ? (a.priority ? b.priority - a.priority : 1) : -1,
      )
    setMostImportantTask(
      uncompletedTasksSorted.find(
        (t) => t.taskType === "respondToChatMessage",
      ) || uncompletedTasksSorted[0],
    )
    setRemainingTasksForToday(uncompletedTasksSorted.slice(1, 3))
  }, [taskContext.tasks])

  if (!calendarContext || !taskContext) return <></>

  return (
    <div className={styles.container}>
      <div className={styles.importantTasksContainer}>
        {calendarEventIdOfCurrentMeeting && (
          <TaskCard
            key={calendarEventIdOfCurrentMeeting}
            calendarEvent={calendarEventsForToday.find(
              (c) => c.eventId === calendarEventIdOfCurrentMeeting,
            )}
          />
        )}
        {!calendarEventIdOfCurrentMeeting &&
          calendarEventsNeedingAction.length > 0 && (
            <TaskCard calendarEvent={calendarEventsNeedingAction[0]} />
          )}
        {!calendarEventIdOfCurrentMeeting &&
          !calendarEventsNeedingAction.length &&
          mostImportantTask && (
            <TaskCard task={mostImportantTask} isFirst={true} />
          )}
      </div>
      {remainingTasksForToday.length > 0 && (
        <div className={styles.tasksForTodayContainer}>
          <h2 className={clsx("title2", styles.sectionTitle)}>
            {"Next in line"}
          </h2>
          <div className={styles.taskCardContainer}>
            {calendarEventsNeedingAction.length > 1 &&
              calendarEventsNeedingAction
                .slice(1)
                .map((event) => (
                  <TaskCard key={event.eventId} calendarEvent={event} />
                ))}
            {remainingTasksForToday.map((task) => (
              <TaskCard key={task.taskId} task={task} />
            ))}
            {calendarEventsForToday.length > 0 &&
              calendarEventsForToday
                .filter(
                  (event) =>
                    event.eventId !== calendarEventIdOfCurrentMeeting &&
                    new Date(event.end).getTime() >= Date.now(),
                )
                .map((event) => (
                  <TaskCard key={event.eventId} calendarEvent={event} />
                ))}
          </div>
        </div>
      )}
    </div>
  )
}

const filterCalendarEventsForToday = (calendarEvents: CalendarEvent[]) => {
  // Get start of today and start of tomorrow for filtering
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const startOfToday = today.toISOString()

  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const startOfTomorrow = tomorrow.toISOString()

  const calendarEventsForToday = calendarEvents.filter(
    (event) => event.start >= startOfToday && event.start < startOfTomorrow,
  )
  return calendarEventsForToday
}

// Return eventId of the event that is currently in progress or starting in the next five minutes
const checkIfCalendarEventCurrentlyInProgressOrStartingInNextFiveMinutes = (
  calendarEvents: CalendarEvent[],
) => {
  const now = new Date()
  const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000)
  return calendarEvents.find((event) => {
    const eventStartDate = new Date(event.start)
    const eventEndDate = new Date(event.end)
    return eventEndDate >= now && eventStartDate <= fiveMinutesFromNow
  })?.eventId
}
